import { Directive, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';

export type WakeLockUnavailableReason = 'unsupported' | 'denied' | 'error';

@Directive({
  selector: '[appScreenWakeLock]',
  standalone: true,
})
export class ScreenWakeLockDirective implements OnChanges, OnDestroy {
  @Input('appScreenWakeLock') active = false;
  @Output() wakeLockUnavailable = new EventEmitter<WakeLockUnavailableReason>();

  private sentinel: WakeLockSentinel | null = null;
  private session = 0;
  private visibilityAttached = false;
  private reportNextFailure = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['active']) return;
    if (this.active) {
      this.acquire();
      return;
    }
    this.release();
  }

  ngOnDestroy(): void {
    this.release();
  }

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;
    void this.request(this.session);
  };

  private acquire(): void {
    const session = ++this.session;
    this.reportNextFailure = true;
    void this.request(session);
  }

  private async request(session: number): Promise<void> {
    if (!this.isSession(session)) return;

    if (!('wakeLock' in navigator)) {
      this.report(session, 'unsupported');
      return;
    }

    this.attachVisibilityListener();
    if (document.visibilityState !== 'visible') return;

    try {
      const next = await navigator.wakeLock.request('screen');
      if (!this.isSession(session)) {
        void next.release().catch(() => undefined);
        return;
      }
      this.replace(next);
      this.reportNextFailure = false;
    } catch (error) {
      if (document.visibilityState !== 'visible') return;
      this.report(session, this.failureReason(error));
    }
  }

  private isSession(session: number): boolean {
    return this.active && session === this.session;
  }

  private attachVisibilityListener(): void {
    if (this.visibilityAttached) return;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.visibilityAttached = true;
  }

  private replace(next: WakeLockSentinel): void {
    const previous = this.sentinel;
    this.sentinel = next;
    if (previous && previous !== next) {
      void previous.release().catch(() => undefined);
    }
  }

  private report(session: number, reason: WakeLockUnavailableReason): void {
    if (!this.reportNextFailure || !this.isSession(session)) return;
    this.reportNextFailure = false;
    this.wakeLockUnavailable.emit(reason);
  }

  private failureReason(error: unknown): WakeLockUnavailableReason {
    const name = error instanceof DOMException ? error.name : '';
    if (name === 'NotSupportedError') return 'unsupported';
    if (name === 'NotAllowedError') return 'denied';
    return 'error';
  }

  private release(): void {
    this.session++;
    this.reportNextFailure = false;
    const current = this.sentinel;
    this.sentinel = null;
    if (current) {
      void current.release().catch(() => undefined);
    }
    if (this.visibilityAttached) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.visibilityAttached = false;
    }
  }
}
